/// <amd-module name="Test/Analytics/Widgets/OverallStateWidgetPropsCreator" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { IODataQueryResponse } from "Analytics/Shared/Common/Types";
import { IOverallStateWidgetProps } from "Test/Analytics/Widgets/OverallStateWidget";
import { TestPointFieldNames, TestResourceEntityNames} from "Test/ManualTestReports/TestReportsCommon/Constants";
import { ITestPointCategoryStringToCountMap, ITestProgressReportFilterState } from "Test/ManualTestReports/TestReportsCommon/Contracts";
import { FilterHelper } from "Test/ManualTestReports/TestReportsCommon/FilterHelper"
import { IFilterState } from "VSSUI/Utilities/Filter";
import { TestProgressReportSourceBase } from "Test/ManualTestReports/TestReportsCommon/TestProgressReportSourceBase";

export class OverallStateWidgetPropsCreator extends TestProgressReportSourceBase {

    public getOverallStateWidgetProps(filterState?: IFilterState, lastSelectedPlan?: number): Promise<IOverallStateWidgetProps> {
        return this._getCurrentFilterState(filterState, lastSelectedPlan).then((currentFilterState: ITestProgressReportFilterState) => {
            const testPointStringToCountMap = this._getTestPointsGroupedByOutcome(currentFilterState);
            return testPointStringToCountMap.then((res: ITestPointCategoryStringToCountMap) => {
                let overallStateWidgetProps = this._getOverallStateWidgetPropsFromTestPointOutcomeCountMap(currentFilterState, res);
                return overallStateWidgetProps;
            });
        });
    }

    private _getOverallStateWidgetPropsFromTestPointOutcomeCountMap(currentFilterState: ITestProgressReportFilterState, testPointCategoryToCountMap: ITestPointCategoryStringToCountMap): IOverallStateWidgetProps {

        let testPlanCount = currentFilterState.selectedPlanIds.length;
        let testPointExecutedCount = 0;
        let testPointPassCount = 0;
        let totalTestPointCount = 0;
        let testPointFailCount = 0;
        let testPointBlockedCount = 0;
        let testPointNotApplicableCount = 0;
        let testPointOtherOutcomeCount = 0;
        let executionPercentageCount = 0;
        let passPercentage = 0;

        testPointPassCount = testPointCategoryToCountMap.Passed;
        testPointFailCount = testPointCategoryToCountMap.Failed;
        testPointBlockedCount = testPointCategoryToCountMap.Blocked;
        testPointNotApplicableCount = testPointCategoryToCountMap.NotApplicable;
        testPointExecutedCount = testPointCategoryToCountMap.Executed;
        executionPercentageCount = testPointCategoryToCountMap.ExecPct;
        totalTestPointCount = testPointCategoryToCountMap.TotalCount;
        passPercentage = testPointCategoryToCountMap.PassedPct;

        testPointOtherOutcomeCount = testPointExecutedCount - testPointPassCount - testPointFailCount - testPointBlockedCount - testPointNotApplicableCount;

        return {
            testPlanCount: testPlanCount,
            testPointCount: totalTestPointCount,
            testPointExecuted: testPointExecutedCount,
            executionPercentageCount: executionPercentageCount,
            passPercentageCount: passPercentage,
            outcomeMappingCount: {
                passedCount: testPointPassCount,
                failedCount: testPointFailCount,
                blockedCount: testPointBlockedCount,
                notApplicableCount: testPointNotApplicableCount,
                otherCount: testPointOtherOutcomeCount,
            }
        } as IOverallStateWidgetProps;
    }

    private _getTestPointsGroupedByOutcome(currentFilterState: ITestProgressReportFilterState): Promise<ITestPointCategoryStringToCountMap> {
        let filterStr = FilterHelper.getFilterString(currentFilterState);
        let groupByStr = this._getGroupByStr(TestPointFieldNames.Outcome);

        const queryOptions = {
            entityType: TestResourceEntityNames.Points,
            project: this._getProjectId()
        } as ODataQueryOptions;

        if (filterStr) {
            queryOptions.$apply = `${filterStr}/${groupByStr}`;
        }
        else {
            queryOptions.$apply = `${groupByStr}`;
        }

        return this.queryOData(queryOptions).then((oDataQueryResponse: IODataQueryResponse) => {
            return this._createTestPointCategoryToCountMap(oDataQueryResponse);
        });
    }

    private _getGroupByStr(fieldName: string) {
        return `aggregate($count as TotalCount,
        cast(LastResultOutcome eq 'Passed', Edm.Int32) with sum as Passed,
        cast(LastResultOutcome eq 'Failed', Edm.Int32) with sum as Failed,
        cast(LastResultOutcome eq 'Blocked', Edm.Int32) with sum as Blocked,
        cast(LastResultOutcome eq 'NotApplicable', Edm.Int32) with sum as NotApplicable,
        cast(LastResultOutcome eq 'None', Edm.Int32) with sum as NotExecuted,
        cast(LastResultOutcome ne 'None', Edm.Int32) with sum as Executed)/compute(Executed mul 100 div TotalCount as ExecPct,
        iif(TotalCount gt NotExecuted, Passed mul 100 div Executed,0) as PassedPct)`;
    }

    private _createTestPointCategoryToCountMap(odataQueryResponse: IODataQueryResponse): ITestPointCategoryStringToCountMap {

        var response: ITestPointCategoryStringToCountMap = {
            Passed: 0,
            Failed: 0,
            Blocked: 0,
            NotApplicable: 0,
            Executed: 0,
            NotExecuted: 0,
            ExecPct: 0,
            PassedPct: 0,
            TotalCount: 0
        } as ITestPointCategoryStringToCountMap;

        if (!odataQueryResponse || !odataQueryResponse.value) {
            throw new Error("Data is empty");
        }

        odataQueryResponse.value.forEach((row: ITestPointCategoryStringToCountMap) => {
            response.Passed = isNaN(row.Passed) ? 0 : row.Passed,
                response.Failed = isNaN(row.Failed) ? 0 : row.Failed,
                response.Blocked = isNaN(row.Blocked) ? 0 : row.Blocked,
                response.NotApplicable = isNaN(row.NotApplicable) ? 0 : row.NotApplicable,
                response.Executed = isNaN(row.Executed) ? 0 : row.Executed,
                response.NotExecuted = isNaN(row.NotExecuted) ? 0 : row.NotExecuted,
                response.ExecPct = isNaN(row.ExecPct) ? 0 : row.ExecPct,
                response.TotalCount = isNaN(row.TotalCount) ? 0 : row.TotalCount,
                response.PassedPct = isNaN(row.PassedPct) ? 0 : row.PassedPct
        });

        return response;
    }
}
